import { useState, useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Wallet, 
  Shield, 
  AlertTriangle, 
  Copy, 
  Check, 
  Eye, 
  EyeOff,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { Button, Input, Modal } from '../ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { cn } from '../../lib/utils';

// Use ethers.js for all wallet generation
import { HDNodeWallet, Wallet as EthersWallet, sha256, ripemd160, getBytes } from 'ethers';

type CryptoNetwork = 'ETHEREUM' | 'BSC' | 'TRON' | 'BITCOIN';

interface NetworkConfig {
  name: string;
  symbol: string;
  icon: string;
  derivationPath: string;
}

const NETWORKS: Record<CryptoNetwork, NetworkConfig> = {
  ETHEREUM: {
    name: 'Ethereum',
    symbol: 'ETH',
    icon: '⟠',
    derivationPath: "m/44'/60'/0'/0/0",
  },
  BSC: {
    name: 'BNB Smart Chain',
    symbol: 'BNB',
    icon: '🔶',
    derivationPath: "m/44'/60'/0'/0/0",
  },
  TRON: {
    name: 'TRON',
    symbol: 'TRX',
    icon: '🔴',
    derivationPath: "m/44'/195'/0'/0/0",
  },
  BITCOIN: {
    name: 'Bitcoin',
    symbol: 'BTC',
    icon: '₿',
    derivationPath: "m/84'/0'/0'/0/0",
  },
};


interface GeneratedWallet {
  network: CryptoNetwork;
  address: string;
  privateKey: string;
  mnemonic: string;
}

interface WalletGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  onSuccess: () => void;
}

// Base58 encoding for Bitcoin/TRON addresses
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function base58Encode(bytes: Uint8Array): string {
  const digits = [0];
  for (const byte of bytes) {
    let carry = byte;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  for (const byte of bytes) {
    if (byte === 0) digits.push(0);
    else break;
  }
  return digits.reverse().map(d => BASE58_ALPHABET[d]).join('');
}

// Bech32 encoding for native SegWit addresses
const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

function bech32Polymod(values: number[]): number {
  const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const v of values) {
    const b = chk >> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) {
      if ((b >> i) & 1) chk ^= GEN[i];
    }
  }
  return chk;
}

function bech32HrpExpand(hrp: string): number[] {
  const ret: number[] = [];
  for (const c of hrp) ret.push(c.charCodeAt(0) >> 5);
  ret.push(0);
  for (const c of hrp) ret.push(c.charCodeAt(0) & 31);
  return ret;
}

function bech32CreateChecksum(hrp: string, data: number[]): number[] {
  const values = bech32HrpExpand(hrp).concat(data).concat([0, 0, 0, 0, 0, 0]);
  const polymod = bech32Polymod(values) ^ 1;
  const ret: number[] = [];
  for (let i = 0; i < 6; i++) ret.push((polymod >> (5 * (5 - i))) & 31);
  return ret;
}

function bech32Encode(hrp: string, data: number[]): string {
  const combined = data.concat(bech32CreateChecksum(hrp, data));
  return hrp + '1' + combined.map(d => BECH32_CHARSET[d]).join('');
}

function convertBits(data: Uint8Array, fromBits: number, toBits: number, pad: boolean): number[] {
  let acc = 0, bits = 0;
  const ret: number[] = [], maxv = (1 << toBits) - 1;
  for (const value of data) {
    acc = (acc << fromBits) | value;
    bits += fromBits;
    while (bits >= toBits) { bits -= toBits; ret.push((acc >> bits) & maxv); }
  }
  if (pad && bits > 0) ret.push((acc << (toBits - bits)) & maxv);
  return ret;
}


// Generate Bitcoin native SegWit address (bc1...) from compressed public key
function generateBitcoinAddress(compressedPubKey: string): string {
  // Hash160 = RIPEMD160(SHA256(publicKey))
  const pubKeyBytes = getBytes(compressedPubKey);
  const sha256Hash = sha256(pubKeyBytes);
  const hash160 = ripemd160(sha256Hash);
  const hash160Bytes = getBytes(hash160);
  
  // Convert to 5-bit groups for bech32
  const words = convertBits(hash160Bytes, 8, 5, true);
  
  // Add witness version (0 for P2WPKH)
  const data = [0].concat(words);
  
  return bech32Encode('bc1', data);
}

// Generate TRON address from Ethereum address
function generateTronAddress(ethAddress: string): string {
  // TRON address = 0x41 + last 20 bytes of keccak256(pubkey) + checksum
  const addressBytes = new Uint8Array(21);
  addressBytes[0] = 0x41; // TRON mainnet prefix
  
  // Get address bytes (remove 0x prefix)
  const ethAddrBytes = getBytes(ethAddress);
  addressBytes.set(ethAddrBytes, 1);
  
  // Double SHA256 for checksum
  const checksum1 = sha256(addressBytes);
  const checksum2 = sha256(checksum1);
  const checksumBytes = getBytes(checksum2);
  
  // Append first 4 bytes of checksum
  const addressWithChecksum = new Uint8Array(25);
  addressWithChecksum.set(addressBytes);
  addressWithChecksum.set(checksumBytes.slice(0, 4), 21);
  
  return base58Encode(addressWithChecksum);
}

export function WalletGenerator({ isOpen, onClose, virtualAccountId, onSuccess }: WalletGeneratorProps) {
  const [step, setStep] = useState<'select' | 'generate' | 'confirm'>('select');
  const [selectedNetwork, setSelectedNetwork] = useState<CryptoNetwork>('ETHEREUM');
  const [generatedWallet, setGeneratedWallet] = useState<GeneratedWallet | null>(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [confirmations, setConfirmations] = useState({
    savedPrivateKey: false,
    savedMnemonic: false,
    understandRisks: false,
  });
  const [label, setLabel] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep('select');
      setGeneratedWallet(null);
      setShowPrivateKey(false);
      setShowMnemonic(false);
      setConfirmations({ savedPrivateKey: false, savedMnemonic: false, understandRisks: false });
      setLabel('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-sensitive]')) e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);


  const generateWallet = useCallback(async () => {
    setIsGenerating(true);
    
    try {
      // Generate wallet using ethers.js
      const wallet = EthersWallet.createRandom();
      const hdWallet = wallet as HDNodeWallet;
      const mnemonic = hdWallet.mnemonic?.phrase || '';
      const privateKey = wallet.privateKey;
      let address: string;

      if (selectedNetwork === 'BITCOIN') {
        // Derive Bitcoin address from the same mnemonic
        // Use compressed public key for SegWit
        const compressedPubKey = wallet.signingKey.compressedPublicKey;
        address = generateBitcoinAddress(compressedPubKey);
      } else if (selectedNetwork === 'TRON') {
        // TRON uses same key derivation as Ethereum but different address format
        address = generateTronAddress(wallet.address);
      } else {
        // Ethereum and BSC use the same address format
        address = wallet.address;
      }

      setGeneratedWallet({ network: selectedNetwork, address, privateKey, mnemonic });
      setStep('generate');
    } catch (error) {
      console.error('Erro ao gerar carteira:', error);
      alert('Erro ao gerar carteira. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedNetwork]);

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const registerMutation = useMutation({
    mutationFn: (data: { virtualAccountId: string; network: string; address: string; label?: string }) =>
      api.post(ENDPOINTS.crypto.registerWallet(data.virtualAccountId), data),
    onSuccess: () => { onSuccess(); onClose(); },
  });

  const handleSaveWallet = () => {
    if (!generatedWallet) return;
    registerMutation.mutate({
      virtualAccountId,
      network: generatedWallet.network,
      address: generatedWallet.address,
      label: label || `Carteira ${NETWORKS[generatedWallet.network].name}`,
    });
  };

  const allConfirmed = confirmations.savedPrivateKey && confirmations.savedMnemonic && confirmations.understandRisks;


  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gerar Nova Carteira" size="lg">
      <div className="space-y-6">
        {step === 'select' && (
          <>
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-400">
                  <p className="font-medium mb-1">Importante - Leia com atenção:</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-400/80">
                    <li>A chave privada será exibida APENAS UMA VEZ</li>
                    <li>Você é responsável por guardar sua chave privada</li>
                    <li>A plataforma NÃO armazena chaves privadas</li>
                    <li>Perder a chave = perder acesso aos fundos</li>
                  </ul>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-3">Selecione a Rede</label>
              <div className="grid grid-cols-2 gap-3">
                {(Object.keys(NETWORKS) as CryptoNetwork[]).map((network) => (
                  <button key={network} onClick={() => setSelectedNetwork(network)}
                    className={cn('p-4 rounded-lg border-2 transition-all text-left',
                      selectedNetwork === network ? 'border-primary-500 bg-primary-500/10' : 'border-surface-700 bg-surface-800/50 hover:border-surface-600')}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{NETWORKS[network].icon}</span>
                      <div>
                        <p className="font-medium text-white">{NETWORKS[network].name}</p>
                        <p className="text-sm text-surface-400">{NETWORKS[network].symbol}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button onClick={generateWallet} disabled={isGenerating}>
                {isGenerating ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Gerando...</>) : (<><Wallet className="w-4 h-4 mr-2" />Gerar Carteira</>)}
              </Button>
            </div>
          </>
        )}


        {step === 'generate' && generatedWallet && (
          <>
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-400">
                  <p className="font-bold mb-1">⚠️ ATENÇÃO - SALVE AGORA!</p>
                  <p>Esta é a ÚNICA vez que você verá sua chave privada e frase de recuperação.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
              <span className="text-2xl">{NETWORKS[generatedWallet.network].icon}</span>
              <div>
                <p className="font-medium text-white">{NETWORKS[generatedWallet.network].name}</p>
                <p className="text-sm text-surface-400">Rede selecionada</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">Endereço Público</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-3 bg-surface-800 rounded-lg text-primary-400 font-mono text-sm break-all">{generatedWallet.address}</code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedWallet.address, 'address')}>
                  {copiedField === 'address' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div data-sensitive>
              <label className="block text-sm font-medium text-surface-300 mb-2">Chave Privada <span className="text-red-400">(NUNCA compartilhe!)</span></label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-red-900/20 border border-red-500/30 rounded-lg overflow-hidden">
                  {showPrivateKey ? (<code className="text-red-400 font-mono text-sm break-all select-none">{generatedWallet.privateKey}</code>) : (<span className="text-surface-500">••••••••••••••••••••••••••••••••</span>)}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowPrivateKey(!showPrivateKey)}>{showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedWallet.privateKey, 'privateKey')}>{copiedField === 'privateKey' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}</Button>
              </div>
            </div>

            <div data-sensitive>
              <label className="block text-sm font-medium text-surface-300 mb-2">Frase de Recuperação (12 palavras)</label>
              <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowMnemonic(!showMnemonic)}>{showMnemonic ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}{showMnemonic ? 'Ocultar' : 'Mostrar'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedWallet.mnemonic, 'mnemonic')}>{copiedField === 'mnemonic' ? <Check className="w-4 h-4 text-green-500 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}Copiar</Button>
                </div>
                {showMnemonic ? (
                  <div className="grid grid-cols-3 gap-2 select-none">
                    {generatedWallet.mnemonic.split(' ').map((word, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-surface-800/50 rounded">
                        <span className="text-xs text-surface-500 w-4">{i + 1}.</span>
                        <span className="text-yellow-400 font-mono text-sm">{word}</span>
                      </div>
                    ))}
                  </div>
                ) : (<p className="text-surface-500 text-center py-4">Clique em "Mostrar" para ver as palavras</p>)}
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('select')}>Voltar</Button>
              <Button onClick={() => setStep('confirm')}>Continuar</Button>
            </div>
          </>
        )}


        {step === 'confirm' && generatedWallet && (
          <>
            <div className="p-4 bg-surface-800/50 rounded-lg">
              <h4 className="font-medium text-white mb-4">Confirme que você salvou suas credenciais:</h4>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={confirmations.savedPrivateKey} onChange={(e) => setConfirmations(prev => ({ ...prev, savedPrivateKey: e.target.checked }))} className="mt-1 w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500" />
                  <span className="text-surface-300">Eu salvei minha <strong className="text-red-400">chave privada</strong> em um local seguro</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={confirmations.savedMnemonic} onChange={(e) => setConfirmations(prev => ({ ...prev, savedMnemonic: e.target.checked }))} className="mt-1 w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500" />
                  <span className="text-surface-300">Eu salvei minha <strong className="text-yellow-400">frase de recuperação</strong> (12 palavras)</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={confirmations.understandRisks} onChange={(e) => setConfirmations(prev => ({ ...prev, understandRisks: e.target.checked }))} className="mt-1 w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500" />
                  <span className="text-surface-300">Eu entendo que a plataforma <strong className="text-white">NÃO armazena</strong> minha chave privada e que perdê-la significa perder acesso aos fundos</span>
                </label>
              </div>
            </div>
            <Input label="Apelido da Carteira (opcional)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`Minha carteira ${NETWORKS[generatedWallet.network].name}`} />
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('generate')}>Voltar</Button>
              <Button onClick={handleSaveWallet} disabled={!allConfirmed || registerMutation.isPending}>
                {registerMutation.isPending ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Salvando...</>) : (<><Lock className="w-4 h-4 mr-2" />Salvar Carteira</>)}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default WalletGenerator;
